import { z } from "zod";
import { businessIdParamSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";
import { monthKeySchema } from "./timetable.ts";

/**
 * Classes per staff member, for pay (Pilates studio shape, T2). A monthly report for the owner and
 * the admins: for each staff member, the classes and the privates they ran, and the people who came.
 * Staff never read it, because it is pay data.
 */

export const staffClassesParamSchema = businessIdParamSchema;
export const staffClassesQuerySchema = z.object({ month: monthKeySchema });
export type StaffClassesQuery = z.infer<typeof staffClassesQuerySchema>;

/**
 * The attendance of one session. `taken`: every booking has a mark, or the staff member saved the
 * names of the people who came. `not_taken`: a booking has no mark yet. `nobody`: nobody was booked
 * and nobody came.
 */
export const staffClassesAttendanceSchema = z.enum(["taken", "not_taken", "nobody"]);
export type StaffClassesAttendance = z.infer<typeof staffClassesAttendanceSchema>;

export const staffClassesSessionSchema = z.object({
  sessionId: uuidSchema,
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  groupId: uuidSchema,
  /** The session's own name, else the group's name. */
  className: z.string(),
  /** A private (the group kind `private`). */
  private: z.boolean(),
  /** The person who ran the session: the cover, when somebody covered. Null when nobody was set. */
  staffUserId: uuidSchema.nullable(),
  /** Confirmed bookings. */
  booked: z.number().int().min(0),
  /** Bookings marked present, plus the names that wait for a parent (they came). */
  present: z.number().int().min(0),
  attendance: staffClassesAttendanceSchema,
});
export type StaffClassesSession = z.infer<typeof staffClassesSessionSchema>;

export const staffClassesRowSchema = z.object({
  staffUserId: uuidSchema.nullable(),
  /** The team name. Null for the row of sessions with nobody set. */
  staffName: z.string().nullable(),
  /** Sessions of a group (not a private). */
  classes: z.number().int().min(0),
  privates: z.number().int().min(0),
  /** People present, in classes and privates together. */
  present: z.number().int().min(0),
  /** Sessions with a booking that has no mark yet. */
  notTaken: z.number().int().min(0),
});
export type StaffClassesRow = z.infer<typeof staffClassesRowSchema>;

export const staffClassesReportSchema = z.object({
  month: monthKeySchema,
  timezone: z.string(),
  /** The report counts the sessions that started before this instant: the end of the month, or now. */
  until: isoDateTimeSchema,
  staff: z.array(staffClassesRowSchema),
  /** Every counted session, by start time. */
  sessions: z.array(staffClassesSessionSchema),
});
export type StaffClassesReport = z.infer<typeof staffClassesReportSchema>;
